import React, { createContext, useContext } from 'react';
import { X } from 'lucide-react';

const DialogContext = createContext(null);

// Dialog Root Component
export const Dialog = ({ children, open, onOpenChange }) => {
  if (!open) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onOpenChange(false);
    }
  };

  return (
    <DialogContext.Provider value={onOpenChange}>
      <div
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        onClick={handleBackdropClick}
      >
        {children}
      </div>
    </DialogContext.Provider>
  );
};

// Dialog Content Component
export const DialogContent = ({ children, className = "" }) => {
  return (
    <div className={`bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto ${className}`}>
      {children}
    </div>
  );
};

// Dialog Header Component: shows a close (X) button unless the header renders its own.
export const DialogHeader = ({ children, className = "", showClose = true }) => {
  const onOpenChange = useContext(DialogContext);
  const withClose = showClose && typeof onOpenChange === 'function';
  return (
    <div className={`relative p-6 border-b border-gray-200 ${withClose ? 'pr-14' : ''} ${className}`}>
      {children}
      {withClose && (
        <button
          type="button"
          onClick={() => onOpenChange(false)}
          aria-label="Close"
          className="absolute right-4 top-4 rounded-md p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
        >
          <X className="w-5 h-5" />
        </button>
      )}
    </div>
  );
};

// Dialog Body Component: padded area between header and footer
export const DialogBody = ({ children, className = "" }) => {
  return (
    <div className={`px-6 py-5 ${className}`}>
      {children}
    </div>
  );
};

// Dialog Title Component
export const DialogTitle = ({ children, className = "" }) => {
  return (
    <h2 className={`text-xl font-semibold text-gray-900 ${className}`}>
      {children}
    </h2>
  );
};

// Dialog Description Component
export const DialogDescription = ({ children, className = "" }) => {
  return (
    <p className={`text-gray-600 mt-1 ${className}`}>
      {children}
    </p>
  );
};

// Dialog Footer Component
export const DialogFooter = ({ children, className = "" }) => {
  return (
    <div className={`p-6 border-t border-gray-200 flex justify-end space-x-3 ${className}`}>
      {children}
    </div>
  );
};

// Dialog Close Button Component
export const DialogClose = ({ children, onClick, className = "" }) => {
  return (
    <button
      onClick={onClick}
      className={`text-gray-400 hover:text-gray-600 transition-colors ${className}`}
    >
      {children || <X className="w-5 h-5" />}
    </button>
  );
};
